import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { useAuth } from "../App";
import { authAxios, logout as authLogout } from "../utils/auth";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { 
  Heart, Users, MessageCircle, Sparkles, Settings, 
  LogOut, Plus, ArrowRight, Calendar, Bell, Brain, Zap, Menu, X, User, Trophy, Coins, Headset, Newspaper, Mail, Eye, MessageSquare, ChevronLeft, ChevronRight
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { useTheme } from "../contexts/ThemeContext";
import AddPersonaModal from "../components/AddPersonaModal";
import NotificationBell from "../components/notifications/NotificationBell";
import AdminMenu from "../components/AdminMenu";
import CoinBalancePill from "../components/CoinBalancePill";
import UpgradeRequiredModal from "../components/UpgradeRequiredModal";
import { useCoinGate } from "../contexts/CoinGateContext";
import { resolveLoginDestination } from "../utils/onboardingTrigger";

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const { theme, resetToDefault } = useTheme();
  const { requireCoins } = useCoinGate();
  const isCyber = theme === "agent_eros_1";
  const [matches, setMatches] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  // Upgrade modal opens when the user hits their subscription-tier
  // relationship cap. Same shape as MatchesGallery for a shared code path.
  const [upgradeState, setUpgradeState] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userPersona, setUserPersona] = useState(null);
  const [chatSessionsCount, setChatSessionsCount] = useState(0);
  const [birthdayReminders, setBirthdayReminders] = useState([]);
  const [fastStartTasks, setFastStartTasks] = useState({});
  // Cached `complete` flag from the server — Dashboard NEVER recomputes Fast
  // Start completion on its own. The flag is written by /profile when a task
  // is marked complete (see backend `_sync_fast_start_complete_flag`).
  const [fastStartComplete, setFastStartComplete] = useState(false);
  const [coinBalance, setCoinBalance] = useState(null);
  // "Relationships Calendar Coming Soon" info modal — triggered by the dimmed
  // calendar icon in the top-right of the Dashboard Reminders panel.
  const [showCalendarComingSoonModal, setShowCalendarComingSoonModal] = useState(false);
  const [claimedButtons, setClaimedButtons] = useState([]);
  
  // Fast Start task definitions with reward references
  const taskOrder = [
    { id: "persona_profile", label: "Persona Profile", rewardRef: "Fast Start persona profile" },
    { id: "relationship_profile", label: "Relationship Profile", rewardRef: "Fast Start relationship profile" },
    { id: "first_contact_agent_eros", label: "First Contact", rewardRef: "Fast Start 1st Agent Eros Chat" },
    { id: "learning_library", label: "Learning Library", rewardRef: "Fast Start Learning Libraries" },
    { id: "style_card", label: "Style Card", rewardRef: "Fast Start Style-Card" },
    { id: "compatibility_report", label: "Compatibility Report", rewardRef: "Fast Start Compatibility" }
  ];
  
  // Fast Start Complete bonus reference
  const FAST_START_COMPLETE_REF = "Fast Start Complete";
  
  // Calculate Fast Start progress
  // `completedTasksCount` is a cheap O(6) filter used only for the "X/6 tasks"
  // label. The authoritative "all complete" boolean comes from the server via
  // `fastStartComplete` (no client-side recalculation).
  const completedTasksCount = taskOrder.filter(t => fastStartTasks[t.id]?.completed).length;
  const totalTasks = taskOrder.length;
  const allTasksComplete = fastStartComplete;
  const nextIncompleteTask = taskOrder.find(t => !fastStartTasks[t.id]?.completed);
  
  // Calculate unclaimed rewards
  const unclaimedTaskRewards = taskOrder.filter(t => 
    fastStartTasks[t.id]?.completed && !claimedButtons.includes(t.rewardRef)
  ).length;
  const unclaimedCompleteBonus = allTasksComplete && !claimedButtons.includes(FAST_START_COMPLETE_REF) ? 1 : 0;
  const totalUnclaimedRewards = unclaimedTaskRewards + unclaimedCompleteBonus;
  
  // Agent Eros Advice modal state
  const [showAdviceModal, setShowAdviceModal] = useState(false);
  const [selectedAdviceMatch, setSelectedAdviceMatch] = useState("none");

  // Forced news popup state
  const [forcedNewsPosts, setForcedNewsPosts] = useState([]);
  const [showForcedNewsModal, setShowForcedNewsModal] = useState(false);
  // Fix B: persona gate. `null` = still checking, `true` = both user_persona
  // and match_persona present (safe to render Dashboard + news side effects),
  // `false` = redirecting to /onboarding (defer all side effects).
  const [personaReady, setPersonaReady] = useState(null);

  // Calculate days until next birthday
  const getDaysUntilBirthday = (dateOfBirth) => {
    if (!dateOfBirth) return null;
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    
    // Set birthday to this year
    let nextBirthday = new Date(today.getFullYear(), birthDate.getMonth(), birthDate.getDate());
    
    // If birthday has passed this year, use next year
    if (nextBirthday < today) {
      nextBirthday = new Date(today.getFullYear() + 1, birthDate.getMonth(), birthDate.getDate());
    }
    
    const diffTime = nextBirthday - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  useEffect(() => {
    fetchData();
    fetchCoinBalance();
  }, []);

  // Fix B: Dashboard-side onboarding guard. Runs on every Dashboard mount
  // (direct nav, refresh, post-login). If the user is missing either a
  // user_persona OR a match_persona (per user direction Q2=b), redirect to
  // /onboarding. Until this resolves, `personaReady` is null and the forced
  // news popup useEffects defer — preventing the phantom view-count race
  // even on a slow network.
  useEffect(() => {
    (async () => {
      const dest = await resolveLoginDestination("/dashboard");
      if (dest === "/onboarding") {
        setPersonaReady(false);
        navigate("/onboarding", { replace: true });
      } else {
        setPersonaReady(true);
      }
    })();
  }, []);

  // Fetch forced news popups on mount (gated on personaReady)
  useEffect(() => {
    if (personaReady !== true) return;
    const fetchForcedNews = async () => {
      try {
        const res = await authAxios.get("/news/forced-unread");
        const posts = res.data?.posts || [];
        if (posts.length > 0) {
          // Sort latest first — digest shows newest at the top.
          const sorted = [...posts].sort(
            (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
          );
          setForcedNewsPosts(sorted);
          setShowForcedNewsModal(true);
        }
      } catch (e) {
        // Silent — a briefly-missed forced popup is fine, a scary toast
        // during transient 429/500 is not. Just log for diagnostics.
        if (e?.response?.status && e.response.status !== 429) {
          console.warn("forced-unread fetch failed:", e.response.status);
        }
      }
    };
    fetchForcedNews();
  }, [personaReady]);

  // Dismiss ALL forced news popups (fired by both READ NEWS + SKIP).
  const dismissAllForcedNews = async () => {
    const posts = forcedNewsPosts;
    setShowForcedNewsModal(false);
    setForcedNewsPosts([]);
    await Promise.all(
      posts.map((p) =>
        authAxios.post(`/news/${p.news_id}/dismiss-popup`).catch(() => null)
      )
    );
  };

  const handleReadForcedNews = async () => {
    await dismissAllForcedNews();
    navigate("/news");
  };

  const handleSkipForcedNews = async () => {
    await dismissAllForcedNews();
  };

  // Format ISO date like "27th July 2026"
  const formatOrdinalDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const day = d.getDate();
    const j = day % 10, k = day % 100;
    const suffix =
      j === 1 && k !== 11 ? "st"
      : j === 2 && k !== 12 ? "nd"
      : j === 3 && k !== 13 ? "rd"
      : "th";
    const month = d.toLocaleString("en-US", { month: "long" });
    return `${day}${suffix} ${month} ${d.getFullYear()}`;
  };

  // NOTE: Previously we re-fetched fast-start / claimed-buttons / coin-balance
  // on every `visibilitychange` event. That fired on every tab focus, causing
  // a 3-API-call storm whenever the user toggled windows. Removed in favour
  // of an explicit refresh on Dashboard mount only — the Fast Start cached
  // flag now lives server-side, so background polling is unnecessary.

  // Fetch coin balance
  const fetchCoinBalance = async () => {
    try {
      const response = await authAxios.get("/credits/balance");
      setCoinBalance(response.data.balance);
    } catch (error) {
      console.error("Failed to fetch coin balance:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [matchesRes, remindersRes, personaRes, sessionsRes, fastStartRes, claimedRes] = await Promise.all([
        authAxios.get("/matches"),
        authAxios.get("/reminders"),
        authAxios.get("/matches/user-persona").catch(() => ({ data: null })),
        authAxios.get("/eroscoach/sessions").catch(() => ({ data: [] })),
        authAxios.get("/profile/fast-start-tasks").catch(() => ({ data: { tasks: {} } })),
        authAxios.get("/credits/claimed-buttons").catch(() => ({ data: { claimed: [] } }))
      ]);
      setMatches(matchesRes.data);
      setReminders(remindersRes.data);
      setUserPersona(personaRes.data);
      setChatSessionsCount(sessionsRes.data?.length || 0);
      setFastStartTasks(fastStartRes.data?.tasks || {});
      // Server-cached "all 6 tasks complete" flag — no client computation.
      setFastStartComplete(Boolean(fastStartRes.data?.complete));
      setClaimedButtons(claimedRes.data?.claimed || []);
      
      // Calculate birthday reminders from matches
      // A match shows up in the dashboard Reminders list only when the user
      // hasn't explicitly disabled it via the per-match reminders toggle
      // (Bell icon on /relationships). Matches without the field default to
      // ON, preserving prior behaviour for existing relationships.
      const matchesWithBirthdays = matchesRes.data
        .filter(m =>
          m.match_type !== "user_persona" &&
          m.date_of_birth &&
          m.reminders_enabled !== false
        )
        .map(m => ({
          match_id: m.match_id,
          name: m.name,
          date_of_birth: m.date_of_birth,
          daysUntil: getDaysUntilBirthday(m.date_of_birth)
        }))
        .filter(m => m.daysUntil !== null)
        .sort((a, b) => a.daysUntil - b.daysUntil)
        .slice(0, 5);
      
      setBirthdayReminders(matchesWithBirthdays);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await authLogout();
      // Reset theme to admin default
      await resetToDefault();
      toast.success(isCyber ? "Signal disconnected" : "Until we meet again...");
      navigate("/");
    } catch (error) {
      toast.error("Logout failed");
    }
  };

  // Handle Agent Eros Advice navigation
  const handleAdviceClick = (e) => {
    e.preventDefault();
    setSelectedAdviceMatch("none");
    setShowAdviceModal(true);
  };

  const handleGetAdvice = () => {
    setShowAdviceModal(false);
    if (selectedAdviceMatch === "none") {
      navigate("/eroscoach");
    } else {
      navigate(`/eroscoach/${selectedAdviceMatch}`);
    }
  };

  // Filter matches for advice dropdown (exclude user_persona)
  const adviceMatches = matches.filter(m => m.match_type !== "user_persona");

  const topMatches = matches
    .filter(m => m.match_type !== "user_persona")
    .sort((a, b) => (b.compatibility_score || 0) - (a.compatibility_score || 0))
    .slice(0, 4);
  const mobileMatches = matches.filter(m => m.match_type !== "user_persona").slice(0, 6);

  const EROS_LOGO_URL = "https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png";
  const mobileMenuItems = [
    { to: "/eroscoach", label: "Agent Eros Advice", iconImg: EROS_LOGO_URL },
    { to: "/relationships", label: "Relationships", icon: Users },
    { to: "/forum", label: "Support Forum", icon: MessageSquare },
    { to: "/news", label: "News & Updates", icon: Newspaper },
    { to: "/affiliate", label: "Affiliate Dashboard", icon: Trophy },
    { to: "/profile", label: "My Account", icon: User },
    { to: "/profile?tab=support", label: "Contact Us", icon: Headset },
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  return (
    <div className="min-h-screen bg-divine-black">
      <Toaster position="top-center" richColors />
      
      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-divine-black-light border-b border-white/10 z-50">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            {isCyber ? (
              <>
                <img
                  src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                  alt="Agent Eros"
                  className="w-6 h-6 object-contain"
                  style={{ animation: "eros-glow-cyan 3s ease-in-out infinite" }}
                />
                <span className="text-lg text-white font-semibold">Agent Eros</span>
              </>
            ) : (
              <>
                <img
                  src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                  alt="Agent Eros"
                  className="w-6 h-6 object-contain"
                  style={{ animation: "eros-glow-gold 3s ease-in-out infinite" }}
                />
                <span className="font-cinzel text-lg text-white">Agent Eros</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <AdminMenu />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-white hover:bg-white/10"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
        
        {/* Mobile Dropdown Menu */}
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-divine-black-light border-b border-white/10 shadow-lg">
            <nav className="py-2">
              {/* Coin Balance Display */}
              <div 
                onClick={() => {
                  navigate("/plans?tab=topup", { state: { from: "/dashboard" } });
                  setMobileMenuOpen(false);
                }}
                className="mx-4 mb-2 flex items-center justify-center gap-2 px-4 py-3 bg-amber-500/10 border border-amber-500/30 rounded-lg cursor-pointer hover:bg-amber-500/20 transition-colors"
                data-testid="mobile-coin-balance"
              >
                <Coins className="w-5 h-5 text-amber-400" />
                <span className="text-amber-400 font-medium">
                  {coinBalance !== null ? coinBalance.toLocaleString() : "—"} coins. Add more
                </span>
              </div>
              
              {mobileMenuItems.map((item) => {
                const Icon = item.icon;
                // Special handling for Agent Eros Advice
                if (item.label === "Agent Eros Advice") {
                  return (
                    <button
                      key={item.to}
                      onClick={(e) => {
                        setMobileMenuOpen(false);
                        handleAdviceClick(e);
                      }}
                      className="w-full flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                      data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <img src={item.iconImg} alt="" className="h-5 w-5 object-contain" />
                      <span>{item.label}</span>
                    </button>
                  );
                }
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-3 text-white/70 hover:bg-white/5 hover:text-white transition-colors"
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    {item.iconImg ? (
                      <img src={item.iconImg} alt="" className="h-5 w-5 object-contain" />
                    ) : (
                      <Icon className="h-5 w-5" />
                    )}
                    <span>{item.label}</span>
                  </Link>
                );
              })}
              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-divine-red hover:bg-divine-red/10 transition-colors"
                data-testid="mobile-nav-logout"
              >
                <LogOut className="h-5 w-5" />
                <span>Logout</span>
              </button>
            </nav>
          </div>
        )}
      </header>

      {/* Mobile Main Content */}
      <main className="md:hidden pt-14 pb-8 px-4 space-y-4">
        {/* Fast Start Panel - Mobile
            Hidden entirely once every task is complete AND every coin bonus
            has been claimed. Per product decision: don't clutter the mobile
            dashboard with a "finished" tile when there's nothing left to do. */}
        {!(allTasksComplete && totalUnclaimedRewards === 0) && (
        <div 
          className={`rounded-xl p-4 cursor-pointer transition-colors ${
            allTasksComplete 
              ? "bg-gradient-to-r from-divine-gold/20 to-divine-gold/10 border border-divine-gold/30" 
              : "glass hover:bg-white/10"
          }`}
          onClick={() => navigate(allTasksComplete ? "/profile" : `/profile?task=${nextIncompleteTask?.id}`)}
          data-testid="mobile-fast-start-panel"
        >
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
              allTasksComplete ? "bg-divine-gold/30" : "bg-purple-500/20"
            }`}>
              {allTasksComplete ? (
                <Trophy className="w-6 h-6 text-divine-gold" />
              ) : (
                <Zap className="w-6 h-6 text-purple-400" />
              )}
            </div>
            <div className="flex-1">
              {allTasksComplete ? (
                <>
                  <div className="flex items-center gap-2">
                    <p className="text-divine-gold font-medium">Fast Start</p>
                    <span className="text-divine-gold/60 text-xs">Complete!</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-white/40 text-sm">{completedTasksCount}/{totalTasks} tasks</p>
                    {totalUnclaimedRewards > 0 && (
                      <span className="flex items-center gap-1 text-amber-400 text-sm animate-pulse">
                        <Coins className="w-3.5 h-3.5" />
                        <span>claim {totalUnclaimedRewards} {totalUnclaimedRewards === 1 ? 'reward' : 'rewards'}</span>
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium">Fast Start</p>
                    <span className="text-purple-400 text-xs">{nextIncompleteTask?.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <p className="text-white/40 text-sm">{completedTasksCount}/{totalTasks} tasks</p>
                    {totalUnclaimedRewards > 0 && (
                      <span className="flex items-center gap-1 text-amber-400 text-sm animate-pulse">
                        <Coins className="w-3.5 h-3.5" />
                        <span>claim {totalUnclaimedRewards} {totalUnclaimedRewards === 1 ? 'reward' : 'rewards'}</span>
                      </span>
                    )}
                  </div>
                </>
              )}
            </div>
            <ArrowRight className="w-5 h-5 text-white/40" />
          </div>
        </div>
        )}

        {/* User Persona Match Panel */}
        {userPersona && (
          <div 
            className="glass rounded-xl p-4 cursor-pointer hover:bg-white/10 transition-colors"
            onClick={() => navigate(`/relationships/${userPersona.match_id}`)}
            data-testid="mobile-user-persona-card"
          >
            <p className="text-white/50 text-xs uppercase tracking-wider mb-3">Your Persona</p>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden">
                {(userPersona.thumbnail_url || userPersona.picture_url || userPersona.picture) ? (
                  <img 
                    src={userPersona.thumbnail_url || userPersona.picture_url || userPersona.picture} 
                    alt={userPersona.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <span className="text-divine-gold font-cinzel">{userPersona.name?.[0]}</span>
                )}
              </div>
              <div className="flex-1">
                <p className="text-white font-medium">{userPersona.name}</p>
                <p className="text-white/40 text-sm">Linked Persona</p>
              </div>
              <ArrowRight className="w-5 h-5 text-white/40" />
            </div>
          </div>
        )}

        {/* Seek Advice Panel */}
        <div className={isCyber ? "cyber-card p-4" : "glass-gold rounded-xl p-4"}>
          <div className="flex items-center gap-3 mb-3">
            <img
              src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
              alt="Agent Eros"
              className="w-8 h-8 object-contain"
              style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
            />
            <h3 className={isCyber ? "text-base text-white font-semibold" : "font-cinzel text-base text-white"}>
              Agent Eros Advice
            </h3>
          </div>
          <p className="text-white/60 text-sm mb-3">
            Get tailored advice on all relationship matters
          </p>
          <Button
            data-testid="mobile-seek-advice-btn"
            onClick={handleAdviceClick}
            className={isCyber 
              ? "w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold rounded-lg glow-effect border-0"
              : "w-full bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
            }
          >
            <MessageCircle className="w-4 h-4 mr-2" />
            {isCyber ? "Agent Eros Advice" : "Agent Eros Advice"}
          </Button>
        </div>

        {/* Your Relationships */}
        <div className={isCyber ? "cyber-card p-4" : "glass-gold rounded-xl p-4"}>
          <h2 className={isCyber ? "text-base text-white font-semibold mb-4" : "font-cinzel text-base text-white mb-4"}>
            Your Relationships
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-divine-gold border-t-transparent rounded-full" />
            </div>
          ) : mobileMatches.length > 0 ? (
            <div className="space-y-3">
              {mobileMatches.map((match) => (
                <div
                  key={match.match_id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                  onClick={() => navigate(`/relationships/${match.match_id}`)}
                  data-testid={`mobile-match-${match.match_id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden">
                    {(match.thumbnail_url || match.picture_url || match.picture) ? (
                      <img 
                        src={match.thumbnail_url || match.picture_url || match.picture} 
                        alt={match.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-divine-gold font-cinzel">{match.name[0]}</span>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{match.name}</p>
                    <p className="text-white/40 text-xs capitalize truncate">{match.relationship_status?.replace(/_/g, " ") || "No status"}</p>
                  </div>
                  {match.compatibility_score && (
                    <div className="text-right">
                      <p className="text-divine-gold font-cinzel text-sm">{match.compatibility_score}%</p>
                    </div>
                  )}
                  <ArrowRight className="w-4 h-4 text-white/40 flex-shrink-0" />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Heart className="w-10 h-10 text-white/20 mx-auto mb-3" />
              <p className="text-white/40 text-sm">No relationships yet</p>
            </div>
          )}
        </div>

        {/* Reminders */}
        <div className="glass rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-cinzel text-base text-white">Reminders</h3>
            <button
              type="button"
              onClick={() => setShowCalendarComingSoonModal(true)}
              className="text-white/30 hover:text-white/60 transition-colors"
              title="Relationships Calendar — coming soon"
              aria-label="Relationships Calendar — coming soon"
              data-testid="reminders-calendar-coming-soon-mobile"
            >
              <Calendar className="w-4 h-4" />
            </button>
          </div>
          {birthdayReminders.length > 0 ? (
            <div className="space-y-2">
              {birthdayReminders.slice(0, 5).map((reminder) => (
                <div 
                  key={reminder.match_id} 
                  className="flex items-center gap-3 cursor-pointer hover:bg-white/5 rounded-lg p-2 -mx-2 transition-colors"
                  onClick={() => navigate(`/relationships/${reminder.match_id}`)}
                >
                  <Calendar className="w-4 h-4 text-divine-gold flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{reminder.name}</p>
                    <p className="text-white/40 text-xs">
                      {new Date(reminder.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Upcoming Birthday
                    </p>
                  </div>
                  <span className={`text-xs font-medium px-2 py-1 rounded-full flex-shrink-0 ${
                    reminder.daysUntil === 0 
                      ? 'bg-divine-gold/20 text-divine-gold' 
                      : reminder.daysUntil <= 7 
                        ? 'bg-divine-red/20 text-divine-red' 
                        : 'bg-white/10 text-white/60'
                  }`}>
                    {reminder.daysUntil === 0 ? 'Today!' : `${reminder.daysUntil}d`}
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-white/40 text-sm">No upcoming reminders</p>
          )}
        </div>

        {/* User Profile Card - Moved to bottom */}
        <div 
          className="glass rounded-xl p-4 cursor-pointer hover:bg-white/10 transition-colors"
          onClick={() => navigate("/profile")}
          data-testid="mobile-user-profile-card"
        >
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
              ) : (
                <span className="text-divine-gold font-cinzel text-xl">{user?.name?.[0]}</span>
              )}
            </div>
            <div className="flex-1">
              <p className="text-white font-medium text-lg">{user?.name}</p>
              <p className="text-white/50 text-sm">{user?.email}</p>
            </div>
            <ArrowRight className="w-5 h-5 text-white/40" />
          </div>
        </div>
      </main>
      
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex fixed left-0 top-0 h-screen w-64 bg-divine-black-light border-r border-white/10 flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
              alt="Agent Eros"
              className="w-8 h-8 object-contain"
              style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
            />
            <span className={isCyber ? "text-xl text-white font-semibold tracking-tight" : "font-cinzel text-xl text-white"}>Agent Eros</span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1">
          <NavItem to="/dashboard" icon={<Sparkles />} label="Dashboard" active />
          {/* Agent Eros Advice - opens modal */}
          <button
            onClick={handleAdviceClick}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-white/60 hover:bg-white/5 hover:text-white transition-colors text-left"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
              alt=""
              className="w-5 h-5 object-contain"
            />
            <span className="font-manrope">Agent Eros Advice</span>
          </button>
          <NavItem to="/relationships" icon={<Users />} label="Relationships" />
          <NavItem to="/forum" icon={<MessageSquare />} label="Support Forum" />
          <NavItem to="/news" icon={<Newspaper />} label="News & Updates" />
          <NavItem to="/affiliate" icon={<Trophy />} label="Affiliate Dashboard" />
          <NavItem to="/profile" icon={<Calendar />} label="My Profile" />
          <NavItem to="/profile?tab=support" icon={<Headset />} label="Contact Us" />
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-divine-gold/20 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-full h-full rounded-full object-cover" />
              ) : (
                <span className="text-divine-gold font-cinzel">{user?.name?.[0]}</span>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm font-medium truncate">{user?.name}</p>
              <p className="text-white/40 text-xs truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="settings-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/settings")}
              className="flex-1 text-white/60 hover:text-white hover:bg-white/5"
            >
              <Settings className="w-4 h-4" />
            </Button>
            <Button
              data-testid="logout-btn"
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="flex-1 text-white/60 hover:text-divine-red hover:bg-divine-red/10"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content - Desktop Only */}
      <main className="hidden md:block ml-64 p-8" data-testid="dashboard-main">
        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className={isCyber ? "text-4xl text-white mb-2 font-bold" : "font-cinzel text-4xl text-white mb-2"}>
              {isCyber ? "Welcome back, " : "Welcome, "}
              <span className={isCyber ? "text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400" : "gold-text"}>
                {user?.name?.split(" ")[0]}
              </span>
            </h1>
            <p className="text-white/60">
              Your relationship intelligence hub
            </p>
          </div>
          <div className="flex items-center gap-3">
            <CoinBalancePill />
            <NotificationBell />
            <AdminMenu />
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
          {/* Fast Start Panel */}
          <div 
            className="glass rounded-xl p-6 cursor-pointer hover:bg-white/10 transition-colors"
            onClick={() => navigate(allTasksComplete ? "/profile" : `/profile?task=${nextIncompleteTask?.id}`)}
            data-testid="fast-start-panel"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                allTasksComplete ? "bg-divine-gold/30" : "bg-purple-500/20"
              }`}>
                {allTasksComplete ? (
                  <Trophy className="w-6 h-6 text-divine-gold" />
                ) : (
                  <Zap className="w-6 h-6 text-purple-400" />
                )}
              </div>
              <span className={`font-medium ${allTasksComplete ? "text-divine-gold" : "text-purple-400"}`}>
                Fast Start
              </span>
            </div>
            {allTasksComplete ? (
              <>
                <p className="text-divine-gold text-sm font-medium mb-1">Complete!</p>
                <div className="flex items-center gap-2">
                  <p className="text-white/40 text-sm">{completedTasksCount}/{totalTasks} tasks</p>
                  {totalUnclaimedRewards > 0 && (
                    <span className="flex items-center gap-1 text-amber-400 text-sm animate-pulse">
                      <Coins className="w-4 h-4" />
                      <span>claim {totalUnclaimedRewards} {totalUnclaimedRewards === 1 ? 'reward' : 'rewards'}</span>
                    </span>
                  )}
                </div>
              </>
            ) : (
              <>
                <p className="text-white/60 text-sm mb-1 truncate">{nextIncompleteTask?.label}</p>
                <div className="flex items-center gap-2">
                  <p className="text-white/40 text-sm">{completedTasksCount}/{totalTasks} tasks</p>
                  {totalUnclaimedRewards > 0 && (
                    <span className="flex items-center gap-1 text-amber-400 text-sm animate-pulse">
                      <Coins className="w-4 h-4" />
                      <span>claim {totalUnclaimedRewards} {totalUnclaimedRewards === 1 ? 'reward' : 'rewards'}</span>
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          
          <StatCard
            icon={<Users className="w-6 h-6" />}
            label="Potential Relationships"
            value={matches.filter(m => m.match_type !== "user_persona").length}
            color="gold"
          />
          <StatCard
            icon={<Eye className="w-6 h-6" />}
            label="Compatibility Analyzed"
            value={matches.filter(m => m.compatibility_score).length}
            color="blue"
          />
          <StatCard
            icon={<MessageCircle className="w-6 h-6" />}
            label="Chat Sessions"
            value={chatSessionsCount}
            color="teal"
          />
          <StatCard
            icon={<Bell className="w-6 h-6" />}
            label="Reminders"
            value={birthdayReminders.length}
            color="gold"
          />
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Top Matches */}
          <div className={isCyber ? "md:col-span-2 cyber-card p-6" : "md:col-span-2 glass-gold rounded-xl p-6"}>
            <div className="flex items-center justify-between mb-6">
              <h2 className={isCyber ? "text-xl text-white font-semibold" : "font-cinzel text-xl text-white"}>Your Relationships</h2>
              <Button
                data-testid="add-match-btn"
                onClick={async () => {
                  if (!(await requireCoins())) return;
                  try {
                    const res = await authAxios.get("/matches/quota");
                    if (res.data?.can_create) {
                      setShowAddModal(true);
                    } else {
                      setUpgradeState({
                        tier: res.data?.tier || "FREE",
                        used: res.data?.used || 0,
                        limit: res.data?.limit ?? 0,
                      });
                    }
                  } catch (err) {
                    // Fail-open: quota check itself errored → let user through,
                    // POST /matches will 402 and RelationshipEditor catches it.
                    console.error("Failed to check relationship quota:", err);
                    setShowAddModal(true);
                  }
                }}
                size="sm"
                className={isCyber ? "bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white rounded-lg glow-effect border-0" : "bg-divine-red hover:bg-divine-red-light text-white rounded-full"}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Relationship
              </Button>
            </div>
            
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
              </div>
            ) : topMatches.length > 0 ? (
              <div className="space-y-4">
                {topMatches.map((match, index) => (
                  <div
                    key={match.match_id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 transition-colors cursor-pointer"
                    onClick={() => navigate(`/relationships/${match.match_id}`)}
                  >
                    <div className="w-12 h-12 rounded-full bg-divine-gold/20 flex items-center justify-center overflow-hidden">
                      {(match.thumbnail_url || match.picture_url || match.picture) ? (
                        <img 
                          src={match.thumbnail_url || match.picture_url || match.picture} 
                          alt={match.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-divine-gold font-cinzel text-lg">{match.name[0]}</span>
                      )}
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-medium">{match.name}</p>
                      <p className="text-white/40 text-sm capitalize">{match.relationship_status?.replace(/_/g, " ") || "No status"}</p>
                    </div>
                    {match.compatibility_score && (
                      <div className="text-right">
                        <p className="text-divine-gold font-cinzel text-lg">{match.compatibility_score}%</p>
                        <p className="text-white/40 text-xs">Match</p>
                      </div>
                    )}
                    <ArrowRight className="w-5 h-5 text-white/40" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Heart className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/40">No relationships yet. Add someone to begin your journey.</p>
                <Button
                  data-testid="empty-add-match-btn"
                  onClick={() => setShowAddModal(true)}
                  className="mt-4 bg-divine-red hover:bg-divine-red-light text-white rounded-full"
                >
                  Add Your First Persona
                </Button>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="space-y-6">
            {/* Social Guidance */}
            <div className={isCyber ? "cyber-card p-6" : "glass-gold rounded-xl p-6"}>
              <div className="flex items-center gap-3 mb-4">
                <img
                  src="https://customer-assets.emergentagent.com/job_c48e0dfd-d860-4af9-851f-221b53f1d8ad/artifacts/h0pj5g64_Agent-Eros-Logo-1.png"
                  alt="Agent Eros"
                  className="w-10 h-10 object-contain"
                  style={{ animation: isCyber ? "eros-glow-cyan 3s ease-in-out infinite" : "eros-glow-gold 3s ease-in-out infinite" }}
                  data-testid="eros-advice-icon"
                />
                <h3 className={isCyber ? "text-lg text-white font-semibold" : "font-cinzel text-lg text-white"}>
                  Agent Eros Advice
                </h3>
              </div>
              <p className="text-white/60 text-sm mb-4">
                Get tailored advice on all relationship matters
              </p>
              <Button
                data-testid="ask-eros-btn"
                onClick={handleAdviceClick}
                className={isCyber 
                  ? "w-full bg-gradient-to-r from-cyan-500 to-purple-500 hover:from-cyan-400 hover:to-purple-400 text-white font-semibold rounded-lg glow-effect border-0"
                  : "w-full bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel rounded-full"
                }
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {isCyber ? "Seek Advice" : "Seek Guidance"}
              </Button>
            </div>

            {/* Reminders */}
            <div className="glass rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-cinzel text-lg text-white">Reminders</h3>
                <button
                  type="button"
                  onClick={() => setShowCalendarComingSoonModal(true)}
                  className="text-white/30 hover:text-white/60 transition-colors"
                  title="Relationships Calendar — coming soon"
                  aria-label="Relationships Calendar — coming soon"
                  data-testid="reminders-calendar-coming-soon-desktop"
                >
                  <Calendar className="w-4 h-4" />
                </button>
              </div>
              {birthdayReminders.length > 0 ? (
                <div className="space-y-3">
                  {birthdayReminders.slice(0, 5).map((reminder) => (
                    <div 
                      key={reminder.match_id} 
                      className="flex items-center gap-3 cursor-pointer hover:bg-white/5 rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/relationships/${reminder.match_id}`)}
                    >
                      <Calendar className="w-4 h-4 text-divine-gold" />
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm truncate">{reminder.name}</p>
                        <p className="text-white/40 text-xs">
                          {new Date(reminder.date_of_birth).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - Upcoming Birthday
                        </p>
                      </div>
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                        reminder.daysUntil === 0 
                          ? 'bg-divine-gold/20 text-divine-gold' 
                          : reminder.daysUntil <= 7 
                            ? 'bg-divine-red/20 text-divine-red' 
                            : 'bg-white/10 text-white/60'
                      }`}>
                        {reminder.daysUntil === 0 ? 'Today!' : `${reminder.daysUntil}d`}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-white/40 text-sm">No upcoming reminders</p>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Add Persona Modal */}
      <AddPersonaModal 
        isOpen={showAddModal} 
        onClose={() => setShowAddModal(false)}
        existingMatches={matches}
      />

      {/* Upgrade-required modal — opened when the user hits their
          subscription-tier relationship cap. */}
      <UpgradeRequiredModal
        state={upgradeState}
        onClose={() => setUpgradeState(null)}
      />

      {/* Agent Eros Advice Modal */}
      <Dialog open={showAdviceModal} onOpenChange={setShowAdviceModal}>
        <DialogContent className="bg-divine-black-light border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white text-lg font-medium">
              Select the relationship you would like advice on
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Select 
              value={selectedAdviceMatch} 
              onValueChange={setSelectedAdviceMatch}
            >
              <SelectTrigger 
                className="w-full bg-white/5 border-white/20 text-white"
                data-testid="advice-match-selector"
              >
                <SelectValue placeholder="Select a relationship" />
              </SelectTrigger>
              <SelectContent className="bg-divine-black-light border-white/10">
                <SelectItem value="none" className="text-white/70">
                  None - General Advice
                </SelectItem>
                {adviceMatches.map((match) => (
                  <SelectItem 
                    key={match.match_id} 
                    value={match.match_id}
                    className="text-white"
                  >
                    {match.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end">
            <Button
              onClick={handleGetAdvice}
              className="bg-divine-gold hover:bg-divine-gold-light text-black font-medium"
              data-testid="seek-advice-btn"
            >
              Seek Advice
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Forced News Digest Popup — condensed "LATEST UPDATES" list.
          READ NEWS routes to /news; SKIP closes. Both dismiss all posts server-side. */}
      <Dialog open={showForcedNewsModal} onOpenChange={(open) => {
        if (!open) handleSkipForcedNews();
      }}>
        <DialogContent
          className="w-[calc(100vw-1rem)] sm:w-full sm:max-w-md bg-divine-black-light border-divine-gold/30 p-6"
          data-testid="forced-news-modal"
        >
          <DialogHeader>
            <DialogTitle className="text-divine-gold text-lg tracking-[0.15em] flex items-center gap-2 font-cinzel">
              <Newspaper className="w-5 h-5 flex-shrink-0" />
              LATEST UPDATES
            </DialogTitle>
          </DialogHeader>

          <ul className="mt-4 space-y-4" data-testid="forced-news-digest">
            {forcedNewsPosts.map((post) => (
              <li
                key={post.news_id}
                className="flex gap-3"
                data-testid={`forced-news-digest-item-${post.news_id}`}
              >
                <span className="text-divine-gold mt-1.5 leading-none">•</span>
                <div className="min-w-0 flex-1">
                  <div className="text-white text-sm sm:text-base font-medium leading-snug break-words">
                    {post.title}
                  </div>
                  <div className="text-white/50 text-xs mt-1">
                    {post.author_name} · {formatOrdinalDate(post.created_at)}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-5 border-t border-white/10 mt-6">
            <Button
              variant="outline"
              onClick={handleSkipForcedNews}
              className="border-white/20 text-white/70 hover:text-white hover:bg-white/5 w-full sm:w-auto"
              data-testid="skip-forced-news-btn"
            >
              SKIP
            </Button>
            <Button
              onClick={handleReadForcedNews}
              className="bg-divine-gold hover:bg-divine-gold-light text-black font-medium tracking-widest w-full sm:w-auto"
              data-testid="read-forced-news-btn"
            >
              READ NEWS
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Relationships Calendar — Coming Soon (info modal) */}
      {showCalendarComingSoonModal && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowCalendarComingSoonModal(false)}
          data-testid="calendar-coming-soon-modal"
        >
          <div
            className={`bg-divine-black-light border rounded-2xl max-w-md w-full p-6 shadow-2xl ${
              isCyber ? "border-cyan-400/30" : "border-divine-gold/30"
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3 ${
                isCyber ? "bg-cyan-400/10" : "bg-divine-gold/10"
              }`}>
                <Calendar className={`w-6 h-6 ${isCyber ? "text-cyan-400" : "text-divine-gold"}`} />
              </div>
              <h3 className={`font-cinzel text-lg ${isCyber ? "text-cyan-400" : "text-divine-gold"}`}>
                Relationships Calendar Coming Soon
              </h3>
            </div>
            <p className="text-white/70 text-sm leading-relaxed mb-4">
              Keep track of upcoming dates, events, anniversaries, touch points and time line reminders.
            </p>
            <Button
              data-testid="calendar-coming-soon-close"
              onClick={() => setShowCalendarComingSoonModal(false)}
              variant="outline"
              className="w-full border-white/20 text-white/60 hover:text-white hover:bg-white/5 rounded-full"
            >
              Close
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Navigation Item Component
const NavItem = ({ to, icon, label, active }) => (
  <Link
    to={to}
    className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
      active 
        ? "bg-divine-gold/20 text-divine-gold" 
        : "text-white/60 hover:text-white hover:bg-white/5"
    }`}
  >
    {icon}
    <span className="font-manrope">{label}</span>
  </Link>
);

// Stat Card Component
const StatCard = ({ icon, label, value, color, onClick }) => {
  const colorClasses = {
    gold: "bg-divine-gold/20 text-divine-gold",
    red: "bg-divine-red/20 text-divine-red",
    teal: "bg-divine-teal/20 text-divine-teal",
    amber: "bg-amber-500/20 text-amber-400",
    blue: "bg-blue-500/20 text-blue-400"
  };

  const baseClasses = "glass rounded-xl p-6";
  const clickableClasses = onClick ? "cursor-pointer hover:bg-white/10 transition-colors" : "";

  return (
    <div 
      className={`${baseClasses} ${clickableClasses}`}
      onClick={onClick}
      data-testid={onClick ? `${label.toLowerCase().replace(/\s+/g, '-')}-card` : undefined}
    >
      <div className={`w-12 h-12 rounded-lg ${colorClasses[color]} flex items-center justify-center mb-4`}>
        {icon}
      </div>
      <p className="text-white/60 text-sm mb-1">{label}</p>
      <p className="text-white text-2xl font-cinzel">{value}</p>
    </div>
  );
};

export default Dashboard;
